import type { BankAccount } from '@/lib/task-accounts';
import { asMarketOutcome, type MarketOutcome } from './market-outcome';

/**
 * PAZARYERI SATIRLARI — CANLI SUNUCUDAN DOGRULANMIS ALAN ADLARI.
 *
 * `GET /v1/tasks`  items[0]:
 *   countdown, countdownDuration, countdownStatus, createdAt, id, isActive,
 *   pretenderUserId, statusUpdatedAt, taskAmount, taskData, taskDate,
 *   taskIsBuying, taskStatusCode, taskTypeCode, updatedAt, userBusinessName,
 *   userFullName, userId
 *   (2026-10-02, yalniz talep sahibine dolu: offerCount, bestOfferPrice,
 *   acceptedOfferPrice, acceptedBusinessName)
 *   (2026-10-02, yalniz piyasa okumasinda: marketOutcome)
 *   (2026-10-03, yalniz piyasa okumasinda: offerByUserId, offerByName — sunucu testiyle
 *   dogrulandi, sunucu degisikligiyle birlikte yayina cikiyor)
 *
 * `GET /v1/offers` items[0]:
 *   countdown, countdownDuration, countdownStatus, createdAt, id, isAccepted,
 *   isDeleted, offerData, offerPrice, offerUserBusinessName, offerUserFullName,
 *   offerUserId, offerUserPhone, organizationId, pretenderUserId, taskAmount,
 *   taskData, taskId, taskIsActive, taskIsBuying, taskStatusCode, taskTypeCode,
 *   taskUserBusinessName, taskUserFullName, taskUserId, taskUserPhone, updatedAt
 *
 * IKI FARK BURADA GORULMEZSE SESSIZ HATA URETIR:
 *   - Talep satirinda sahip alani `userId`, `taskUserId` DEGIL.
 *   - Teklif satirinda `statusUpdatedAt` YOK; geri sayim `countdown` uzerinden
 *     mutlaklastirilir (bkz. src/lib/task-phase.ts).
 *
 * ============================ KIMLIKLER STRING =============================
 * pg `int8`'i JSON'a STRING olarak yazar. `id`, `taskId`, `offerUserId`,
 * `pretenderUserId`, `userId` bu yuzden `string`. Number()'a cevirip === ile
 * karsilastirmak 2^53 ustunde sessizce yanlis sonuc verir; karsilastirma HER
 * ZAMAN `String(a) === String(b)` (bkz. sameId).
 *
 * =========================== offer_price BIRIM KUR ==========================
 * `offerPrice` 1 BIRIM dovizin fiyatidir, islemin toplami DEGILDIR.
 * Toplam = offerPrice * taskAmount ve yalniz `totalOf()` ile uretilir
 * (src/lib/money.ts). `offerPrice` ve `taskAmount` ayni satirda geldigi icin
 * carpim istemcide yapilabiliyor.
 */

/**
 * Kimlik/metin alanini GUVENLE dizeye cevirir.
 *
 * `String(unknown)` yazilsaydi bir nesne '[object Object]' olur ve iki farkli
 * nesne EsIT gorunurdu — kimlik karsilastirmasinda felaket. Yalniz dize, sayi
 * ve bigint kabul ediliyor; ikisi de pg'nin `int8`/`text` icin uretebilecegi
 * tek sekiller.
 */
const asText = (value: unknown): string | null => {
  if (typeof value === 'string') return value === '' ? null : value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'bigint') return String(value);
  return null;
};

/** pg int8 karsilastirmasinin TEK dogru yolu. */
export const sameId = (a: unknown, b: unknown): boolean => {
  const left = asText(a);
  const right = asText(b);
  return left !== null && left === right;
};

export type SelectedAccountHolder = { selectedAccount?: BankAccount } | null;

export type TaskRow = {
  id: string;
  taskTypeCode: string;
  taskIsBuying: boolean;
  taskAmount: number;
  /** 'new' | 'pending' | 'cancelled' | 'warning' … */
  taskStatusCode: string | null;
  /** Talep sahibinin kimligi. Sunucudaki alan adi `userId`. */
  taskUserId: string | null;
  /** Kabul edilen buronun kimligi. Dolu ise gorev karara baglanmis. */
  pretenderUserId: string | null;
  createdAt: string | null;
  /** Fazin baslangici — geri sayim hesabinin BIRINCIL girdisi. */
  statusUpdatedAt: string | null;
  countdownStatus: string | null;
  countdown: number | null;
  countdownDuration: number | null;
  /** Talep sahibi. */
  userFullName: string | null;
  userBusinessName: string | null;
  taskData: SelectedAccountHolder;
  /**
   * ASAGIDAKI DORDU HER YANITTA YOK.
   *
   * Duz `/v1/tasks` listesinde gelmiyorlar; piyasa gorunumunde
   * (`taskPretenderCurrencyUserId=<buro>`) sunucu BURONUN teklifini satira ekliyor
   * (2026-10-03, bir buro bir teklif): cagiranin kendi teklifi oncelikli, yoksa organizasyonun
   * herhangi bir uyesinin canli teklifi. Yani `offerUserId` dolu ama cagiran DEGILSE teklif bir
   * IS ARKADASININ (bkz. ./offer-ownership.ts). `null` MEVCUT DEGIL demektir — "teklif
   * verilmedi" ile "bu sorguda sorulmadi" ayni degere dusuyor; arayuz bunu yalniz piyasa
   * ekraninda okuyor, orada anlami tektir.
   *
   *   offerUserId   : teklifin SAHIBI (ilk veren; is arkadasi guncelleyince degismez)
   *   offerByUserId : teklifi EN SON yazan (veren ya da son guncelleyen)
   *   offerByName   : onun adi soyadi ("Son guncelleyen")
   *
   * Son ikisi eski sunucuda gelmez: `null`.
   */
  offerPrice: number | null;
  offerUserId: string | null;
  offerByUserId: string | null;
  offerByName: string | null;
  /**
   * TALEP SAHIBININ OZETI (2026-10-02, M6) — Taleplerim karti teklif sayisini, en iyi kuru ve
   * kazanani ayri bir ekrana gitmeden gostersin diye. Sunucu YALNIZ talep sahibine (ve musteri
   * org yoneticisine) doldurur; baska herkese ve buro listelerine `null`. `null` "bu sorguda
   * bilinmiyor" demek, 0 DEGIL — "0 teklif" yazmak yanlis bilgi olurdu.
   *
   *   offerCount           : silinmemis teklif sayisi
   *   bestOfferPrice       : YONE BAGLI en iyi BIRIM KUR — musteri ALIYORSA (taskIsBuying) en
   *                          dusuk, SATIYORSA en yuksek
   *   acceptedOfferPrice   : kazanan teklifin BIRIM KURU (karar yoksa null)
   *   acceptedBusinessName : kazanan buronun adi (organizasyon, yoksa kisi)
   *
   * Kurlar pg `numeric` oldugu icin STRING gelir ("34.10"); num() cevirir.
   */
  offerCount: number | null;
  bestOfferPrice: number | null;
  acceptedOfferPrice: number | null;
  acceptedBusinessName: string | null;
  /**
   * PIYASA SONUCU (2026-10-02) — karar sonrasi bu buro icin ne oldu (bkz. ./market-outcome.ts).
   * Yalniz piyasa okumasinda (`taskPretenderCurrencyUserId`) dolu; talep acikken, baska
   * okumalarda ve eski sunucuda `null`. Bilinmeyen deger de `null`.
   */
  marketOutcome: MarketOutcome | null;
};

export type OfferRow = {
  id: string;
  taskId: string;
  /** Teklifi VEREN buro. */
  offerUserId: string;
  /** BIRIM KUR. Toplam icin totalOf(unitRate(offerPrice), taskAmount). */
  offerPrice: number;
  /** Gorevin kabul ettigi buro; gorev basina tek deger, her satirda AYNI. */
  pretenderUserId: string | null;
  /** Sunucu hesapliyor: BU teklif kazanan mi. */
  isAccepted: boolean;
  isDeleted: boolean;
  taskTypeCode: string;
  taskIsBuying: boolean;
  taskAmount: number;
  taskStatusCode: string | null;
  taskUserId: string | null;
  createdAt: string | null;
  /**
   * Teklif satirinda SUNUCU GONDERMIYOR — alan tiplerde duruyor ki faz hesabi
   * tek imzayla calissin; degeri her zaman null olacak ve `countdown` yolu
   * devreye girecek.
   */
  statusUpdatedAt: string | null;
  countdownStatus: string | null;
  countdown: number | null;
  countdownDuration: number | null;
  /** Teklifi veren buro. */
  offerUserFullName: string | null;
  offerUserBusinessName: string | null;
  /** Talebi acan musteri. */
  taskUserFullName: string | null;
  taskUserBusinessName: string | null;
  offerData: SelectedAccountHolder;
  taskData: SelectedAccountHolder;
};

/* -------------------------------------------------------------------------- */
/* Ham yanit -> satir                                                          */
/* -------------------------------------------------------------------------- */

type Raw = Record<string, unknown>;

const str = asText;

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  // pg `numeric` alanlari da STRING gelebilir. Number() burada guvenli, cunku
  // bunlar TUTAR — kimlik degil.
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = asText(value);
  if (text === null) return null;
  const parsed = Number(text.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const bool = (value: unknown): boolean => value === true || value === 'true' || value === 1;

const accountHolder = (value: unknown): SelectedAccountHolder => {
  if (typeof value !== 'object' || value === null) return null;
  const selected = (value as Raw).selectedAccount;
  if (typeof selected !== 'object' || selected === null) return null;
  const account = selected as Raw;
  const code = str(account.accountCode);
  const currency = str(account.accountCurrency);
  if (code === null || currency === null) return null;

  const label = str(account.accountLabel);
  const name = str(account.accountName);
  const iban = str(account.accountIbanCode);
  return {
    selectedAccount: {
      accountCode: code,
      accountCurrency: currency,
      // `exactOptionalPropertyTypes`: eksik alan `undefined` DEGIL, HIC OLMAMALI.
      ...(label === null ? {} : { accountLabel: label }),
      ...(name === null ? {} : { accountName: name }),
      ...(iban === null ? {} : { accountIbanCode: iban }),
    },
  };
};

export const toTaskRow = (raw: Raw): TaskRow => ({
  id: str(raw.id) ?? '',
  taskTypeCode: str(raw.taskTypeCode) ?? '',
  taskIsBuying: bool(raw.taskIsBuying),
  taskAmount: num(raw.taskAmount) ?? 0,
  taskStatusCode: str(raw.taskStatusCode),
  // `userId` DOGRULANMIS ad; `taskUserId` yalniz geri donus.
  taskUserId: str(raw.userId) ?? str(raw.taskUserId),
  pretenderUserId: str(raw.pretenderUserId),
  createdAt: str(raw.createdAt) ?? str(raw.taskDate),
  statusUpdatedAt: str(raw.statusUpdatedAt),
  countdownStatus: str(raw.countdownStatus),
  countdown: num(raw.countdown),
  countdownDuration: num(raw.countdownDuration),
  userFullName: str(raw.userFullName),
  userBusinessName: str(raw.userBusinessName),
  taskData: accountHolder(raw.taskData),
  offerPrice: num(raw.offerPrice),
  offerUserId: str(raw.offerUserId),
  offerByUserId: str(raw.offerByUserId),
  offerByName: str(raw.offerByName),
  offerCount: num(raw.offerCount),
  bestOfferPrice: num(raw.bestOfferPrice),
  acceptedOfferPrice: num(raw.acceptedOfferPrice),
  acceptedBusinessName: str(raw.acceptedBusinessName),
  marketOutcome: asMarketOutcome(raw.marketOutcome),
});

export const toOfferRow = (raw: Raw): OfferRow => ({
  id: str(raw.id) ?? `${str(raw.taskId) ?? ''}:${str(raw.offerUserId) ?? ''}`,
  taskId: str(raw.taskId) ?? '',
  offerUserId: str(raw.offerUserId) ?? '',
  offerPrice: num(raw.offerPrice) ?? 0,
  pretenderUserId: str(raw.pretenderUserId),
  // Sunucu `isAccepted`'i hesapliyor; kimlik karsilastirmasi yalniz geri donus
  // ve o da STRING uzerinden yapiliyor.
  isAccepted: bool(raw.isAccepted) || sameId(raw.pretenderUserId, raw.offerUserId),
  isDeleted: bool(raw.isDeleted),
  taskTypeCode: str(raw.taskTypeCode) ?? '',
  taskIsBuying: bool(raw.taskIsBuying),
  taskAmount: num(raw.taskAmount) ?? 0,
  taskStatusCode: str(raw.taskStatusCode),
  taskUserId: str(raw.taskUserId),
  createdAt: str(raw.createdAt),
  statusUpdatedAt: str(raw.statusUpdatedAt),
  countdownStatus: str(raw.countdownStatus),
  countdown: num(raw.countdown),
  countdownDuration: num(raw.countdownDuration),
  offerUserFullName: str(raw.offerUserFullName),
  offerUserBusinessName: str(raw.offerUserBusinessName),
  taskUserFullName: str(raw.taskUserFullName),
  taskUserBusinessName: str(raw.taskUserBusinessName),
  offerData: accountHolder(raw.offerData),
  taskData: accountHolder(raw.taskData),
});

/** Karsi tarafin gorunur adi — sirket adi varsa o, yoksa kisi adi. */
export const counterpartyName = (
  businessName: string | null,
  fullName: string | null,
): string | null => businessName ?? fullName;
